// lib/pricing/priceBands.ts
// Pure math for the sidebar's price bands ("0-20", "150+") — no I/O, no React.
//
// Lives here rather than in the price-map module because the price bands are a
// B2C search feature that must not depend on the (dormant) B2B price-map flow.
// The band TABLE and its labels live with the sidebar in
// components/search/searchHelpers.ts; this file is only the arithmetic, so that
// server code can use it without importing a component module.

/** One price constraint: either bound may be null (open-ended). Max is
 *  INCLUSIVE here — matching the long-standing client-side display filter. */
export interface PriceInterval {
  min: number | null;
  max: number | null;
}

/** Parse a sidebar band key into an interval: "0-20" → {0,20}, "150+" → {150,null}.
 *  Unparseable parts become null bounds (open-ended) rather than NaN — a naive
 *  '-'-split once produced `price < NaN`, which Meilisearch matches against
 *  NOTHING (found 2026-07-21). */
export function parseBandKey(range: string): PriceInterval {
  if (range.endsWith('+')) {
    const min = parseFloat(range);
    return { min: isNaN(min) ? null : min, max: null };
  }
  const [minStr, maxStr] = range.split('-');
  const min = parseFloat(minStr);
  const max = parseFloat(maxStr);
  return { min: isNaN(min) ? null : min, max: isNaN(max) ? null : max };
}

/** Intersect two intervals (most-restrictive bounds). Null = empty intersection. */
export function intersectIntervals(a: PriceInterval, b: PriceInterval): PriceInterval | null {
  const min = a.min === null ? b.min : b.min === null ? a.min : Math.max(a.min, b.min);
  const max = a.max === null ? b.max : b.max === null ? a.max : Math.min(a.max, b.max);
  if (min !== null && max !== null && min > max) return null;
  return { min, max };
}

/** Is the price inside ANY of the intervals? An empty list matches nothing
 *  (= an active-but-impossible filter, e.g. typed "εως 5" ∩ band 40-60). */
export function priceInIntervals(num: number, intervals: PriceInterval[]): boolean {
  return intervals.some(
    ({ min, max }) => (min === null || num >= min) && (max === null || num <= max),
  );
}
